<template>
  <div class="rg">
    <header class="rg__head">
      <div>
        <h1 class="rg__title">Мониторинг реестров</h1>
        <p class="rg__lead">
          Плановые даты считаются по условиям договора и календарю «День Х». Строка подсвечивается, если факт отстаёт от плана.
        </p>
      </div>
      <div class="rg__controls">
        <select v-model.number="customerId" class="rg__select" @change="reload">
          <option v-for="c in customers" :key="c.id" :value="c.id">{{ c.name }}{{ c.has_terms ? '' : ' (нет условий)' }}</option>
        </select>
        <input v-model="dateFrom" type="date" class="rg__input" title="Дата загрузки реестра с" @change="loadRegistries">
        <input v-model="dateTo" type="date" class="rg__input" title="по" @change="loadRegistries">
        <button class="rg__btn" type="button" :disabled="pending" @click="reload">{{ pending ? 'Обновляем…' : 'Обновить' }}</button>
        <router-link class="rg__btn" :to="{ name: 'RegistrySettings', query: { customer: customerId } }">Настройки</router-link>
      </div>
    </header>

    <div v-if="error" class="rg__error">{{ error }}</div>

    <section v-if="alerts.length" class="rg__alerts">
      <h2 class="rg__h2">Сигналы</h2>
      <div v-for="(a, i) in alerts" :key="i" :class="['alert', `alert--${a.key === 'readiness' || a.key === 'approval' ? 'warning' : 'critical'}`]">
        <span class="alert__text">{{ a.text }}</span>
        <router-link class="alert__link" :to="{ name: 'RegistryControl', params: { id: a.registry_id } }">Открыть</router-link>
      </div>
    </section>

    <section v-if="!terms.length && customerId" class="rg__empty">
      Для этого заказчика не заданы условия договора — плановые даты не считаются.
      <router-link :to="{ name: 'RegistrySettings', query: { customer: customerId } }">Задать условия</router-link>
    </section>

    <div v-if="canEdit" class="rg__actions">
      <button class="rg__btn rg__btn--primary" type="button" :disabled="pending || !calendarCount" @click="planYear">
        Проставить план на год
      </button>
      <span class="rg__hint">{{ calendarCount ? `В календаре «День Х» ${calendarCount} дат` : 'Календарь «День Х» пуст — загрузите его в настройках' }}</span>
      <button class="rg__btn" type="button" @click="showCreate = !showCreate">+ Реестр вручную</button>
    </div>

    <form v-if="showCreate" class="rg__create" @submit.prevent="createRegistry">
      <label>Дата загрузки реестра <input v-model="draft.planned_load_date" type="date" required class="rg__input"></label>
      <label>Транспорт
        <select v-model="draft.fleet" class="rg__select"><option v-for="f in FLEETS" :key="f.value" :value="f.value">{{ f.label }}</option></select>
      </label>
      <label>Номер <input v-model="draft.number" class="rg__input" placeholder="можно позже"></label>
      <button class="rg__btn rg__btn--primary" type="submit">Создать</button>
    </form>

    <div v-if="message" class="rg__ok">{{ message }}</div>

    <div class="rg__table-wrap">
      <table class="rg__table">
        <thead>
          <tr>
            <th>Реестр №</th>
            <th>Транспорт</th>
            <th>Дата загрузки реестра</th>
            <th>Рейсов / сумма без НДС</th>
            <th>Статус реестра (заказчика)</th>
            <th>Счёт №, дата, сумма</th>
            <th>Статус счёта</th>
            <th>Отправка реестра (план)</th>
            <th>Комплект: план</th>
            <th>Комплект: факт</th>
            <th>Бух. документы в ЭДО</th>
            <th>Проверка заводом (расчёт)</th>
            <th>Согласование по тарифам и ТСД</th>
            <th>Предел оригиналов (30 дн.)</th>
            <th>Счёт в оплате?</th>
            <th>Платёжный день АЛТ</th>
            <th>Платёжный день заказчика</th>
            <th>Факт оплаты</th>
            <th>Отклонения</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in registries" :key="r.id" :class="`row--${rowTone(r.flags)}`">
            <td><b>{{ r.number || '—' }}</b></td>
            <td>{{ r.fleet === 'own' ? 'Свой' : 'Наёмный' }}</td>
            <td>{{ formatDate(r.planned_load_date) }}</td>
            <td>{{ r.trips_count }} / {{ formatMoney(r.trips_amount) }}</td>
            <td><EditCell :row="r" field="customer_status" :edit="canEdit" @save="save" /></td>
            <td>
              <EditCell :row="r" field="invoice_number" :edit="canEdit" placeholder="№" @save="save" />
              <EditCell :row="r" field="invoice_date" type="date" :edit="canEdit" @save="save" />
              <EditCell :row="r" field="invoice_amount" type="number" :edit="canEdit" placeholder="сумма" @save="save" />
            </td>
            <td><EditCell :row="r" field="invoice_status" :edit="canEdit" @save="save" /></td>
            <td><EditCell :row="r" field="planned_send_date" type="date" :edit="canEdit" @save="save" /></td>
            <td>{{ formatDate(r.plan && r.plan.send_docs_by) }}</td>
            <td><EditCell :row="r" field="docs_sent_date" type="date" :edit="canEdit" @save="save" /></td>
            <td><EditCell :row="r" field="edo_sent_date" type="date" :edit="canEdit" @save="save" /></td>
            <td>{{ formatDate(r.plan && r.plan.review_until) }}</td>
            <td>
              <EditCell :row="r" field="approval_date" type="date" :edit="canEdit" @save="save" />
              <EditCell :row="r" field="approval_comment" :edit="canEdit" placeholder="комментарий" @save="save" />
            </td>
            <td>
              {{ formatDate(r.plan && r.plan.originals_deadline) }}
              <div v-if="r.plan" :class="['slack', r.plan.slack_days < 0 ? 'slack--bad' : '']">запас {{ r.plan.slack_days }} дн.</div>
            </td>
            <td>
              <select v-if="canEdit" :value="r.invoice_in_payment === null ? '' : String(r.invoice_in_payment)" class="rg__select rg__select--sm"
                      @change="save(r, 'invoice_in_payment', $event.target.value === '' ? null : $event.target.value === 'true')">
                <option value="">—</option><option value="true">да</option><option value="false">нет</option>
              </select>
              <span v-else>{{ r.invoice_in_payment === null ? '—' : (r.invoice_in_payment ? 'да' : 'нет') }}</span>
            </td>
            <td><b>{{ formatDate(r.plan && r.plan.payment_date) }}</b></td>
            <td><EditCell :row="r" field="customer_payment_date" type="date" :edit="canEdit" @save="save" /></td>
            <td>
              <EditCell :row="r" field="actual_payment_date" type="date" :edit="canEdit" @save="save" />
              <EditCell :row="r" field="payment_comment" :edit="canEdit" placeholder="комментарий" @save="save" />
            </td>
            <td class="flags">
              <div v-for="f in r.flags" :key="f.key" :class="`flag flag--${f.level}`">{{ f.text }}</div>
            </td>
            <td>
              <router-link class="rg__btn rg__btn--sm" :to="{ name: 'RegistryControl', params: { id: r.id } }">Контроль</router-link>
            </td>
          </tr>
          <tr v-if="!registries.length && !pending"><td colspan="20" class="rg__placeholder">Реестров за период нет.</td></tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, h, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { registryApi, errorText } from '@/backend/registryApi.js'
import { FLEETS, formatDate, formatMoney, rowTone } from './registryLogic.mjs'

// Ячейка с правкой по месту: показывает значение, по клику — поле ввода.
const EditCell = defineComponent({
  props: { row: Object, field: String, type: { type: String, default: 'text' }, edit: Boolean, placeholder: String },
  emits: ['save'],
  setup(props, { emit }) {
    const editing = ref(false)
    const value = ref('')
    const shown = () => {
      const v = props.row[props.field]
      if (v === null || v === undefined || v === '') return props.edit ? (props.placeholder || '—') : '—'
      if (props.type === 'date') return formatDate(v)
      if (props.type === 'number') return formatMoney(v)
      return v
    }
    const start = () => { if (!props.edit) return; value.value = props.row[props.field] ?? ''; editing.value = true }
    const finish = () => {
      editing.value = false
      const next = value.value === '' ? null : value.value
      if (next !== (props.row[props.field] ?? null)) emit('save', props.row, props.field, next)
    }
    return () => editing.value
      ? h('input', { class: 'cell-input', type: props.type, value: value.value, autofocus: true,
        onInput: (e) => { value.value = e.target.value }, onBlur: finish,
        onKeydown: (e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') editing.value = false } })
      : h('div', { class: ['cell', props.edit ? 'cell--edit' : '', props.row[props.field] ? '' : 'cell--empty'], onClick: start }, shown())
  },
})

export default {
  name: 'RegistryMonitorPage',
  components: { EditCell },
  setup() {
    const store = useStore()
    const route = useRoute()
    const roleName = computed(() => store.state.user.user?.role?.name)
    const canEdit = computed(() => ['superadmin', 'Админ'].includes(roleName.value) || store.getters.hasPermission('registry.edit'))
    const customers = ref([])
    const customerId = ref(null)
    const terms = ref([])
    const calendarCount = ref(0)
    const registries = ref([])
    const alerts = ref([])
    const pending = ref(false)
    const error = ref('')
    const message = ref('')
    const showCreate = ref(false)
    const today = new Date()
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    const dateFrom = ref(iso(new Date(today.getFullYear(), today.getMonth() - 2, 1)))
    const dateTo = ref(iso(new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())))
    const draft = ref({ planned_load_date: '', fleet: 'hired', number: '' })

    async function loadRegistries() {
      if (!customerId.value) return
      registries.value = await registryApi.registries(customerId.value, dateFrom.value, dateTo.value)
    }

    async function reload() {
      if (!customerId.value) return
      pending.value = true
      error.value = ''
      try {
        const [t, cal, a] = await Promise.all([
          registryApi.terms(customerId.value), registryApi.calendar(customerId.value), registryApi.alerts(customerId.value),
        ])
        terms.value = t
        calendarCount.value = cal.length
        alerts.value = a
        await loadRegistries()
      } catch (e) {
        error.value = errorText(e)
      } finally {
        pending.value = false
      }
    }

    async function save(row, field, value) {
      error.value = ''
      try {
        const updated = await registryApi.updateRegistry(row.id, { [field]: value })
        Object.assign(row, updated)
      } catch (e) {
        error.value = errorText(e)
      }
    }

    async function planYear() {
      pending.value = true
      try {
        const result = await registryApi.plan({ customer_id: customerId.value, months: 12 })
        message.value = result.created ? `Добавлено плановых реестров: ${result.created}` : 'Новых плановых реестров нет — план уже проставлен'
        await reload()
      } catch (e) {
        error.value = errorText(e)
      } finally {
        pending.value = false
      }
    }

    async function createRegistry() {
      try {
        await registryApi.createRegistry({ customer_id: customerId.value, ...draft.value, number: draft.value.number || null })
        showCreate.value = false
        draft.value = { planned_load_date: '', fleet: 'hired', number: '' }
        await reload()
      } catch (e) {
        error.value = errorText(e)
      }
    }

    onMounted(async () => {
      try {
        customers.value = await registryApi.customers()
        const wanted = Number(route.query.customer)
        const preferred = customers.value.find((c) => c.id === wanted) || customers.value.find((c) => c.has_terms)
          || customers.value.find((c) => /эфес|efes/i.test(c.name)) || customers.value[0]
        customerId.value = preferred ? preferred.id : null
        await reload()
      } catch (e) {
        error.value = errorText(e)
      }
    })

    return {
      FLEETS, alerts, calendarCount, canEdit, createRegistry, customerId, customers, dateFrom, dateTo, draft, error,
      formatDate, formatMoney, loadRegistries, message, pending, planYear, registries, reload, rowTone, save,
      showCreate, terms,
    }
  },
}
</script>

<style scoped>
.rg { padding: 24px 20px 80px; display: flex; flex-direction: column; gap: 14px; color: #111827; }
.rg__head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; }
.rg__title { margin: 0; font-size: 1.5rem; font-weight: 700; }
.rg__lead { margin: 6px 0 0; font-size: 0.9rem; color: #6b7280; max-width: 760px; }
.rg__controls, .rg__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.rg__select, .rg__input { border: 1px solid #d1d5db; border-radius: 8px; padding: 7px 10px; font: inherit; font-size: 0.875rem; background: #fff; }
.rg__select--sm { padding: 3px 6px; font-size: 0.8rem; }
.rg__btn { border: 1px solid #d1d5db; background: #fff; border-radius: 9999px; padding: 7px 14px; font: inherit; font-size: 0.875rem; cursor: pointer; color: #111827; text-decoration: none; white-space: nowrap; }
.rg__btn:disabled { opacity: 0.6; cursor: default; }
.rg__btn--primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
.rg__btn--sm { padding: 4px 10px; font-size: 0.8rem; }
.rg__hint { color: #6b7280; font-size: 0.85rem; }
.rg__h2 { margin: 0 0 6px; font-size: 1rem; }
.rg__error { border: 1px solid #fecaca; background: #fef2f2; color: #7f1d1d; border-radius: 10px; padding: 10px 14px; }
.rg__ok { border: 1px solid #bbf7d0; background: #f0fdf4; color: #166534; border-radius: 10px; padding: 10px 14px; }
.rg__empty { border: 1px dashed #d1d5db; border-radius: 10px; padding: 12px 14px; color: #374151; }
.rg__create { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fafafa; }
.rg__create label { display: flex; flex-direction: column; gap: 4px; font-size: 0.8rem; color: #374151; }
.rg__alerts { display: flex; flex-direction: column; gap: 6px; }
.alert { display: flex; justify-content: space-between; gap: 12px; align-items: center; border-radius: 10px; padding: 8px 12px; font-size: 0.875rem; border-left: 4px solid; }
.alert--warning { background: #fffbeb; border-color: #d97706; }
.alert--critical { background: #fef2f2; border-color: #dc2626; }
.alert__link { white-space: nowrap; color: #4f46e5; }
.rg__table-wrap { overflow: auto; border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; max-height: 72vh; }
.rg__table { border-collapse: collapse; width: max-content; min-width: 100%; font-size: 0.82rem; }
.rg__table th { position: sticky; top: 0; background: #f9fafb; text-align: left; font-weight: 600; color: #374151; padding: 8px; border-bottom: 1px solid #e5e7eb; max-width: 140px; white-space: normal; vertical-align: bottom; z-index: 1; }
.rg__table td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.row--warning td { background: #fffbeb; }
.row--critical td { background: #fef2f2; }
.flags { min-width: 220px; }
.flag { font-size: 0.78rem; margin-bottom: 2px; }
.flag--critical { color: #b91c1c; font-weight: 600; }
.flag--warning { color: #b45309; }
.slack { font-size: 0.75rem; color: #15803d; }
.slack--bad { color: #b91c1c; font-weight: 600; }
.rg__placeholder { text-align: center; color: #6b7280; padding: 24px; }
:deep(.cell) { min-height: 18px; }
:deep(.cell--edit) { cursor: pointer; border-radius: 4px; }
:deep(.cell--edit:hover) { background: #eef2ff; }
:deep(.cell--empty) { color: #9ca3af; }
:deep(.cell-input) { width: 130px; border: 1px solid #6366f1; border-radius: 4px; padding: 2px 4px; font: inherit; }
</style>
